import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes fallback

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);
  const isAuthenticatedRef = useRef(false);
  const timeoutMsRef = useRef(DEFAULT_TIMEOUT_MS);

  // Fetch settings timeout on login
  const fetchTimeout = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/settings`);
      const mins = res.data.session_timeout_minutes || 30;
      timeoutMsRef.current = mins * 60 * 1000;
    } catch {
      timeoutMsRef.current = DEFAULT_TIMEOUT_MS;
    }
  }, []);

  useEffect(() => {
    if (token) {
      // Check if session expired while tab was closed
      const lastActivity = localStorage.getItem('lastActivity');
      const savedTimeout = localStorage.getItem('sessionTimeoutMs');
      const checkTimeout = savedTimeout ? parseInt(savedTimeout, 10) : DEFAULT_TIMEOUT_MS;
      if (lastActivity && (Date.now() - parseInt(lastActivity, 10)) > checkTimeout) {
        localStorage.removeItem('token');
        localStorage.removeItem('lastActivity');
        localStorage.removeItem('sessionTimeoutMs');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setLoading(false);
        window.location.href = '/login?reason=idle';
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Idle timeout logic
  const resetIdleTimer = useCallback(() => {
    if (!isAuthenticatedRef.current) return;
    localStorage.setItem('lastActivity', Date.now().toString());
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      if (isAuthenticatedRef.current) {
        logout();
        window.location.href = '/login?reason=idle';
      }
    }, timeoutMsRef.current);
  }, []);

  useEffect(() => {
    isAuthenticatedRef.current = !!user;
    if (user) {
      // Fetch configurable timeout and start timer
      fetchTimeout().then(() => {
        localStorage.setItem('sessionTimeoutMs', timeoutMsRef.current.toString());
        const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
        resetIdleTimer();
      });
      return () => {
        const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach(e => window.removeEventListener(e, resetIdleTimer));
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      };
    } else {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    }
  }, [user, resetIdleTimer, fetchTimeout]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('lastActivity', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('sessionTimeoutMs');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const updateUserData = (newData) => {
    setUser(prev => ({ ...prev, ...newData }));
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const isAdmin = () => hasRole(['admin']);
  const isManager = () => hasRole(['admin', 'manager']);
  const needsPasswordSetup = () => user && !user.password_initialized;

  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    login,
    logout,
    updateUserData,
    hasRole,
    isAdmin,
    isManager,
    needsPasswordSetup,
    isAuthenticated: !!user
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, token, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
